import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectOption } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import ResponsiveTable from '../ResponsiveTable';
import { formatCurrency, formatDate } from '../../lib/utils';
import { updatePaymentData, updateDeveloperData, updateExpenseData } from '../../services/projectService';
import { 
  CreditCard, 
  Users, 
  Receipt,
  Edit,
  Check,
  X,
  DollarSign,
  Calendar,
  CreditCard as PaymentIcon
} from 'lucide-react';

const ProjectDataTables = ({ 
  project, 
  onPaymentStatus, 
  onDeveloperPayment, 
  onExpensePayment 
}) => {
  const [editDialog, setEditDialog] = useState({ open: false, type: null, data: null });
  const [editForm, setEditForm] = useState({});

  // Handle edit payment
  const handleEditPayment = (payment) => {
    setEditForm({
      amount: payment.amount || 0,
      due_date: payment.due_date || payment.payment_date || '',
      payment_channel: payment.payment_channel || 'transfer'
    });
    setEditDialog({ open: true, type: 'payment', data: payment });
  };

  // Handle edit developer
  const handleEditDeveloper = (developer) => {
    setEditForm({
      developer_name: developer.developer_name || developer.name || '',
      amount: developer.amount || 0,
      percentage: developer.percentage || 0,
      payment_mode: developer.payment_mode || 'after_completion',
      payment_date: developer.payment_date || '',
      payment_channel: developer.payment_channel || 'transfer'
    });
    setEditDialog({ open: true, type: 'developer', data: developer });
  };

  // Handle edit expense
  const handleEditExpense = (expense) => {
    setEditForm({
      expense_name: expense.expense_name || expense.name || '',
      amount: expense.amount || 0,
      description: expense.description || '',
      expense_date: expense.expense_date || expense.date || '',
      payment_channel: expense.payment_channel || expense.channel || 'transfer'
    });
    setEditDialog({ open: true, type: 'expense', data: expense });
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      if (editDialog.type === 'payment') {
        // อัปเดตข้อมูลการชำระเงิน
        const { error } = await updatePaymentData(
          project.id,
          editDialog.data.id,
          {
            due_date: editForm.due_date,
            payment_channel: editForm.payment_channel
          }
        );
        
        if (error) {
          throw new Error(error.message || 'Failed to update payment');
        }
        
        // อัปเดต project state (ต้องส่ง callback ไปยัง parent component)
        if (typeof onPaymentStatus === 'function') {
          // ส่ง callback เพื่อ refresh ข้อมูล
          window.location.reload(); // หรือใช้ callback ที่เหมาะสม
        }
        
      } else if (editDialog.type === 'developer') {
        // อัปเดตข้อมูล Developer
        const { error } = await updateDeveloperData(
          project.id,
          editDialog.data.id,
          {
            developer_name: editForm.developer_name,
            payment_date: editForm.payment_date
          }
        );
        
        if (error) {
          throw new Error(error.message || 'Failed to update developer');
        }
        
        // อัปเดต project state
        if (typeof onDeveloperPayment === 'function') {
          window.location.reload(); // หรือใช้ callback ที่เหมาะสม
        }
        
      } else if (editDialog.type === 'expense') {
        // อัปเดตข้อมูลค่าใช้จ่าย
        const { error } = await updateExpenseData(
          project.id,
          editDialog.data.id,
          {
            expense_name: editForm.expense_name,
            amount: editForm.amount,
            description: editForm.description,
            expense_date: editForm.expense_date,
            payment_channel: editForm.payment_channel
          }
        );
        
        if (error) {
          throw new Error(error.message || 'Failed to update expense');
        }
        
        // อัปเดต project state
        if (typeof onExpensePayment === 'function') {
          window.location.reload(); // หรือใช้ callback ที่เหมาะสม
        }
      }
      
      setEditDialog({ open: false, type: null, data: null });
      setEditForm({});
      
    } catch (error) {
      console.error('Error saving edit:', error);
      alert(`เกิดข้อผิดพลาดในการบันทึกข้อมูล: ${error.message}`);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditDialog({ open: false, type: null, data: null });
    setEditForm({});
  };

  // Calculate payment details
  const calculatePaymentDetails = (payment) => {
    const vat = (() => {
      try {
        return typeof project?.vat === 'string' ? JSON.parse(project.vat) : project?.vat;
      } catch (e) {
        return { enabled: true, percentage: 7, amount: 0 };
      }
    })();

    const withholdingTax = (() => {
      try {
        return typeof project?.withholding_tax === 'string' ? JSON.parse(project.withholding_tax) : project?.withholding_tax;
      } catch (e) {
        return { enabled: true, percentage: 3, amount: 0 };
      }
    })();

    const discount = (() => {
      try {
        return typeof project?.discount === 'string' ? JSON.parse(project.discount) : project?.discount;
      } catch (e) {
        return { enabled: false, type: 'percentage', percentage: 0, amount: 0 };
      }
    })();

    // คำนวณส่วนลดสำหรับงวดนี้
    const projectPrice = project?.project_price || 0;
    const totalDiscountAmount = discount.enabled 
      ? (discount.type === 'percentage' 
          ? (projectPrice * (discount.percentage || 0)) / 100
          : (discount.amount || 0))
      : 0;
    
    const priceAfterDiscount = projectPrice - totalDiscountAmount;
    // ราคาหลังหักส่วนลด = ราคารวม VAT แล้ว (ต้องแยก VAT ออกมา)
    const totalWithVat = priceAfterDiscount;
    const priceBeforeVat = vat.enabled ? totalWithVat / (1 + (vat.percentage || 0) / 100) : totalWithVat;
    const totalRevenue = totalWithVat;
    const paymentPercentage = totalRevenue > 0 ? (payment.amount / totalRevenue) : 0;
    
    // คำนวณ VAT และ WHT ตามตัวอย่างที่ถูกต้อง
    const installmentDiscountAmount = totalDiscountAmount * paymentPercentage;
    const installmentTotalWithVat = (totalWithVat * paymentPercentage);
    const installmentPriceBeforeVat = (priceBeforeVat * paymentPercentage);
    const vatAmount = vat.enabled ? installmentTotalWithVat - installmentPriceBeforeVat : 0;
    const whtAmount = withholdingTax.enabled 
      ? (withholdingTax.type === 'amount' 
          ? (withholdingTax.amount * paymentPercentage)
          : (installmentPriceBeforeVat * (withholdingTax.percentage || 0)) / 100)
      : 0;
    
    // ยอดรับชำระ = ยอดรวม - WHT เท่านั้น (ไม่หัก VAT)
    const receivedAmount = payment.amount - whtAmount;
    
    return {
      totalAmount: payment.amount,
      vatAmount,
      whtAmount,
      receivedAmount, // ยอดรับชำระ (ไม่หัก VAT แต่หัก WHT)
      installmentDiscountAmount,
      percentageOfTotal: paymentPercentage * 100
    };
  };
  // Payment Schedule Table
  const paymentColumns = [
    { 
      header: 'งวด', 
      key: 'installment_number', 
      render: (row) => (
        <div>
          <p className="font-semibold">งวด {row.installment_number || row.installment}</p>
          <p className="text-xs text-muted-foreground">#{row.id?.slice(-6) || 'N/A'}</p>
        </div>
      )
    },
    { 
      header: 'ประเภทการชำระ', 
      key: 'type', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {row.type === 'percentage' ? 'เปอร์เซ็นต์' : 'จำนวนเงิน'}
          </span>
          <p className="text-xs text-muted-foreground mt-1">
            {row.type === 'percentage' ? `${row.percentage}% ของยอดรวม` : 'ยอดคงที่'}
          </p>
        </div>
      )
    },
    { 
      header: 'ยอดรวม', 
      key: 'total_amount', 
      align: 'right', 
      render: (row) => {
        const details = calculatePaymentDetails(row);
        return (
          <div className="text-right">
            <p className="font-semibold">{formatCurrency(details.totalAmount)}</p>
            <p className="text-xs text-muted-foreground">
              รวม VAT ยังไม่หัก WHT
            </p>
          </div>
        );
      }
    },
    { 
      header: 'ส่วนลด', 
      key: 'discount', 
      align: 'right', 
      render: (row) => {
        const details = calculatePaymentDetails(row);
        return (
          <div className="text-right">
            {details.installmentDiscountAmount > 0 ? (
              <>
                <p className="font-semibold text-yellow-600">-{formatCurrency(details.installmentDiscountAmount)}</p>
                <p className="text-xs text-muted-foreground">
                  ส่วนลดงวดนี้
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">-</p>
            )}
          </div>
        );
      }
    },
    { 
      header: 'ยอดรับชำระ', 
      key: 'received_amount', 
      align: 'right', 
      render: (row) => {
        const details = calculatePaymentDetails(row);
        return (
          <div className="text-right">
            <p className="font-semibold text-green-700">{formatCurrency(details.receivedAmount)}</p>
            <p className="text-xs text-muted-foreground">
              หลังหัก WHT เท่านั้น
            </p>
          </div>
        );
      }
    },
    { 
      header: 'VAT', 
      key: 'vat', 
      align: 'right', 
      render: (row) => {
        const details = calculatePaymentDetails(row);
        const vat = (() => {
          try {
            return typeof project?.vat === 'string' ? JSON.parse(project.vat) : project?.vat;
          } catch (e) {
            return { enabled: true, percentage: 7, amount: 0 };
          }
        })();
        return (
          <div className="text-right">
            <p className="font-semibold">{formatCurrency(details.vatAmount)}</p>
            <p className="text-xs text-muted-foreground">
              {vat.percentage}% VAT
            </p>
          </div>
        );
      }
    },
    { 
      header: 'WHT', 
      key: 'wht', 
      align: 'right', 
      render: (row) => {
        const details = calculatePaymentDetails(row);
        const withholdingTax = (() => {
          try {
            return typeof project?.withholding_tax === 'string' ? JSON.parse(project.withholding_tax) : project?.withholding_tax;
          } catch (e) {
            return { enabled: true, percentage: 3, amount: 0 };
          }
        })();
        return (
          <div className="text-right">
            <p className="font-semibold">{formatCurrency(details.whtAmount)}</p>
            <p className="text-xs text-muted-foreground">
              {withholdingTax.percentage}% WHT
            </p>
          </div>
        );
      }
    },
    { 
      header: 'ช่องทางการชำระ', 
      key: 'payment_channel', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {row.payment_channel || row.channel || 'transfer'}
          </span>
        </div>
      )
    },
    { 
      header: 'วันที่ครบกำหนด', 
      key: 'due_date', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <p className="font-medium">{formatDate(row.due_date || row.payment_date)}</p>
          {row.due_date && (
            <p className="text-xs text-muted-foreground">
              {new Date(row.due_date) < new Date() ? 'เลยกำหนด' : 'ยังไม่ครบกำหนด'}
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'สถานะ', 
      key: 'status', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <Badge 
            variant={row.paid ? "default" : "secondary"}
            className={row.paid ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}
          >
            {row.paid ? 'ชำระแล้ว' : 'รอชำระ'}
          </Badge>
          {row.paid && row.paid_at && (
            <p className="text-xs text-muted-foreground mt-1">
              ชำระเมื่อ: {formatDate(row.paid_at)}
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'การดำเนินการ', 
      key: 'action', 
      align: 'center', 
      render: (row) => (
        <div className="flex space-x-2 justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPaymentStatus(row.id, !row.paid)}
            className={`w-10 h-10 p-0 ${row.paid ? "text-red-600 border-red-200 hover:bg-red-50" : "text-green-600 border-green-200 hover:bg-green-50"}`}
            title={row.paid ? 'ยกเลิกการชำระ' : 'ยืนยันการชำระ'}
          >
            {row.paid ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEditPayment(row)}
            className="w-10 h-10 p-0 text-blue-600 border-blue-200 hover:bg-blue-50"
            title="แก้ไขข้อมูล"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  // Developer Payments Table
  const developerColumns = [
    { 
      header: 'ชื่อ Developer', 
      key: 'developer_name', 
      render: (row) => (
        <div>
          <p className="font-semibold">{row.developer_name || row.name}</p>
          <p className="text-xs text-muted-foreground">#{row.id?.slice(-6) || 'N/A'}</p>
        </div>
      )
    },
    { 
      header: 'ประเภทการจ่าย', 
      key: 'payment_mode', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {row.payment_mode === 'per_installment' ? 'ตามงวด' : 
             row.payment_mode === 'after_completion' ? 'หลังเสร็จ' : 
             row.payment_mode === 'percentage' ? 'เปอร์เซ็นต์' : 'ยอดคงที่'}
          </span>
          {row.percentage && row.percentage > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {row.percentage}% ของยอดรวม
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'จำนวนเงิน', 
      key: 'amount', 
      align: 'right', 
      render: (row) => (
        <div className="text-right">
          <p className="font-semibold">{formatCurrency(row.amount)}</p>
          {row.percentage && row.percentage > 0 && (
            <p className="text-xs text-muted-foreground">
              {row.percentage}% ของยอดรวม
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'หักภาษี', 
      key: 'withholding_tax', 
      align: 'right', 
      render: (row) => {
        const withholdingTax = typeof row.withholding_tax === 'object' ? row.withholding_tax : { amount: 0, percentage: 3 };
        return (
          <div className="text-right">
            <p className="font-semibold">{formatCurrency(withholdingTax.amount || 0)}</p>
            <p className="text-xs text-muted-foreground">
              {withholdingTax.percentage || 3}% WHT
            </p>
          </div>
        );
      }
    },
    { 
      header: 'ยอดสุทธิ', 
      key: 'net_amount', 
      align: 'right', 
      render: (row) => (
        <div className="text-right">
          <p className="font-semibold text-green-700">{formatCurrency(row.net_amount)}</p>
          <p className="text-xs text-muted-foreground">
            หลังหักภาษี
          </p>
        </div>
      )
    },
    { 
      header: 'ช่องทางการจ่าย', 
      key: 'payment_channel', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {row.payment_channel || row.channel || 'transfer'}
          </span>
        </div>
      )
    },
    { 
      header: 'สถานะ', 
      key: 'status', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <Badge 
            variant={row.paid ? "default" : "secondary"}
            className={row.paid ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}
          >
            {row.paid ? 'จ่ายแล้ว' : 'รอจ่าย'}
          </Badge>
          {row.paid && row.paid_at && (
            <p className="text-xs text-muted-foreground mt-1">
              จ่ายเมื่อ: {formatDate(row.paid_at)}
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'วันที่จ่าย', 
      key: 'payment_date', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <p className="font-medium">{formatDate(row.payment_date)}</p>
          {row.payment_date && (
            <p className="text-xs text-muted-foreground">
              {new Date(row.payment_date) < new Date() ? 'เลยกำหนด' : 'ยังไม่ครบกำหนด'}
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'การดำเนินการ', 
      key: 'action', 
      align: 'center', 
      render: (row) => (
        <div className="flex space-x-2 justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDeveloperPayment(row.id, !row.paid)}
            className={`w-10 h-10 p-0 ${row.paid ? "text-red-600 border-red-200 hover:bg-red-50" : "text-green-600 border-green-200 hover:bg-green-50"}`}
            title={row.paid ? 'ยกเลิกการจ่าย' : 'ยืนยันการจ่าย'}
          >
            {row.paid ? <X className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEditDeveloper(row)}
            className="w-10 h-10 p-0 text-blue-600 border-blue-200 hover:bg-blue-50"
            title="แก้ไขข้อมูล"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  // Other Expenses Table
  const expenseColumns = [
    { 
      header: 'ชื่อค่าใช้จ่าย', 
      key: 'name', 
      render: (row) => (
        <div>
          <p className="font-semibold">{row.name || row.expense_name}</p>
          <p className="text-xs text-muted-foreground">#{row.id?.slice(-6) || 'N/A'}</p>
        </div>
      )
    },
    { 
      header: 'จำนวนเงิน', 
      key: 'amount', 
      align: 'right', 
      render: (row) => (
        <div className="text-right">
          <p className="font-semibold">{formatCurrency(row.amount)}</p>
          <p className="text-xs text-muted-foreground">
            ค่าใช้จ่าย
          </p>
        </div>
      )
    },
    { 
      header: 'รายละเอียด', 
      key: 'description', 
      render: (row) => (
        <div>
          <p className="text-sm">{row.description || '-'}</p>
          {row.description && (
            <p className="text-xs text-muted-foreground mt-1">
              รายละเอียดเพิ่มเติม
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'วันที่เกิดค่าใช้จ่าย', 
      key: 'expense_date', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <p className="font-medium">{formatDate(row.date || row.expense_date)}</p>
          <p className="text-xs text-muted-foreground">
            วันที่เกิดค่าใช้จ่าย
          </p>
        </div>
      )
    },
    { 
      header: 'ช่องทางการจ่าย', 
      key: 'payment_channel', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {row.channel || row.payment_channel || 'transfer'}
          </span>
        </div>
      )
    },
    { 
      header: 'สถานะ', 
      key: 'status', 
      align: 'center', 
      render: (row) => (
        <div className="text-center">
          <Badge 
            variant={row.paid ? "default" : "secondary"}
            className={row.paid ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}
          >
            {row.paid ? 'จ่ายแล้ว' : 'รอจ่าย'}
          </Badge>
          {row.paid && row.paid_at && (
            <p className="text-xs text-muted-foreground mt-1">
              จ่ายเมื่อ: {formatDate(row.paid_at)}
            </p>
          )}
        </div>
      )
    },
    { 
      header: 'การดำเนินการ', 
      key: 'action', 
      align: 'center', 
      render: (row) => (
        <div className="flex space-x-2 justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExpensePayment(row.id, !row.paid)}
            className={`w-10 h-10 p-0 ${row.paid ? "text-red-600 border-red-200 hover:bg-red-50" : "text-green-600 border-green-200 hover:bg-green-50"}`}
            title={row.paid ? 'ยกเลิกการจ่าย' : 'ยืนยันการจ่าย'}
          >
            {row.paid ? <X className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEditExpense(row)}
            className="w-10 h-10 p-0 text-blue-600 border-blue-200 hover:bg-blue-50"
            title="แก้ไขข้อมูล"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Payment Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <span>ตารางการชำระเงิน</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!project?.payment_schedule || project.payment_schedule.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">ยังไม่มีการแบ่งงวดชำระ</p>
            </div>
          ) : (
            <>
              <ResponsiveTable
                data={project.payment_schedule.sort((a, b) => (a.installment_number || a.installment) - (b.installment_number || b.installment))}
                columns={paymentColumns}
              />
              
              {/* Payment Schedule Summary */}
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
              <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center space-x-2">
                <CreditCard className="h-5 w-5" />
                <span>สรุปตารางการชำระเงิน</span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-800">
                    {formatCurrency(project.payment_schedule.reduce((sum, payment) => sum + (payment.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-blue-600">ยอดรวมทั้งหมด</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-800">
                    {formatCurrency(project.payment_schedule.filter(p => p.paid).reduce((sum, payment) => sum + (payment.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-green-600">ชำระแล้ว</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-800">
                    {formatCurrency(project.payment_schedule.filter(p => !p.paid).reduce((sum, payment) => sum + (payment.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-orange-600">ค้างชำระ</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-800">
                    {project.payment_schedule.length}
                  </p>
                  <p className="text-sm text-purple-600">จำนวนงวด</p>
                </div>
              </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Developer Payments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>ค่าจ้าง Developer</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!project?.developer_payments || project.developer_payments.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">ไม่มีข้อมูล Developer</p>
            </div>
          ) : (
            <>
              <ResponsiveTable
                data={project.developer_payments}
                columns={developerColumns}
              />
              
              {/* Developer Payments Summary */}
              <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
              <h4 className="text-lg font-semibold text-green-800 mb-4 flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>สรุปค่าจ้าง Developer</span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-800">
                    {formatCurrency(project.developer_payments.reduce((sum, dev) => sum + (dev.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-green-600">ยอดรวมทั้งหมด</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-800">
                    {formatCurrency(project.developer_payments.filter(d => d.paid).reduce((sum, dev) => sum + (dev.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-blue-600">จ่ายแล้ว</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-800">
                    {formatCurrency(project.developer_payments.filter(d => !d.paid).reduce((sum, dev) => sum + (dev.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-orange-600">รอจ่าย</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-800">
                    {project.developer_payments.length}
                  </p>
                  <p className="text-sm text-purple-600">จำนวน Developer</p>
                </div>
              </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Other Expenses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Receipt className="h-5 w-5" />
            <span>ค่าใช้จ่ายโปรเจค</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!project?.other_expenses || project.other_expenses.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">ไม่มีค่าใช้จ่ายโปรเจค</p>
            </div>
          ) : (
            <>
              <ResponsiveTable
                data={project.other_expenses}
                columns={expenseColumns}
              />
              
              {/* Other Expenses Summary */}
              <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-200">
              <h4 className="text-lg font-semibold text-orange-800 mb-4 flex items-center space-x-2">
                <Receipt className="h-5 w-5" />
                <span>สรุปค่าใช้จ่ายโปรเจค</span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-800">
                    {formatCurrency(project.other_expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-orange-600">ยอดรวมทั้งหมด</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-800">
                    {formatCurrency(project.other_expenses.filter(e => e.paid).reduce((sum, expense) => sum + (expense.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-green-600">จ่ายแล้ว</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-800">
                    {formatCurrency(project.other_expenses.filter(e => !e.paid).reduce((sum, expense) => sum + (expense.amount || 0), 0))}
                  </p>
                  <p className="text-sm text-red-600">รอจ่าย</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-800">
                    {project.other_expenses.length}
                  </p>
                  <p className="text-sm text-purple-600">จำนวนรายการ</p>
                </div>
              </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Edit Modal */}
      <Dialog open={editDialog.open} onOpenChange={handleCancelEdit}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          {/* Header with gradient background */}
          <div className={`relative overflow-hidden rounded-t-lg ${
            editDialog.type === 'payment' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
            editDialog.type === 'developer' ? 'bg-gradient-to-r from-green-500 to-green-600' :
            'bg-gradient-to-r from-orange-500 to-orange-600'
          }`}>
            <div className="absolute inset-0 bg-black/10"></div>
            <DialogHeader className="relative p-6 pb-4">
              <DialogTitle className="flex items-center space-x-3 text-white text-2xl font-bold">
                {editDialog.type === 'payment' && (
                  <>
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <PaymentIcon className="h-6 w-6" />
                    </div>
                    <span>แก้ไขการชำระเงิน</span>
                  </>
                )}
                {editDialog.type === 'developer' && (
                  <>
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Users className="h-6 w-6" />
                    </div>
                    <span>แก้ไขค่าจ้าง Developer</span>
                  </>
                )}
                {editDialog.type === 'expense' && (
                  <>
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Receipt className="h-6 w-6" />
                    </div>
                    <span>แก้ไขค่าใช้จ่ายโปรเจค</span>
                  </>
                )}
              </DialogTitle>
              <DialogDescription className="text-blue-100 text-sm mt-2">
                {editDialog.type === 'payment' && 'แก้ไขข้อมูลการชำระเงิน - สามารถแก้ไขได้เฉพาะวันที่และช่องทางการชำระ'}
                {editDialog.type === 'developer' && 'แก้ไขข้อมูลค่าจ้าง Developer - สามารถแก้ไขได้เฉพาะชื่อและวันที่จ่าย'}
                {editDialog.type === 'expense' && 'แก้ไขข้อมูลค่าใช้จ่ายโปรเจค'}
              </DialogDescription>
            </DialogHeader>
          </div>
          
          {/* Form Content */}
          <div className="p-6 space-y-6">
            {editDialog.type === 'payment' && (
              <div className="space-y-6">
                {/* Due Date Card */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="due_date" className="text-lg font-semibold text-blue-800">
                        วันที่ครบกำหนด
                      </Label>
                      <p className="text-sm text-blue-600">เลือกวันที่ที่ต้องการชำระเงิน</p>
                    </div>
                  </div>
                  <Input
                    id="due_date"
                    type="date"
                    value={editForm.due_date || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                {/* Payment Channel Card */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <PaymentIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="payment_channel" className="text-lg font-semibold text-green-800">
                        ช่องทางการชำระ
                      </Label>
                      <p className="text-sm text-green-600">เลือกวิธีการชำระเงิน</p>
                    </div>
                  </div>
                  <Select
                    value={editForm.payment_channel || 'transfer'}
                    onChange={(e) => setEditForm(prev => ({ ...prev, payment_channel: e.target.value }))}
                    className="w-full border-green-300 focus:border-green-500 focus:ring-green-500"
                  >
                    <SelectOption value="transfer">💳 โอนเงิน</SelectOption>
                    <SelectOption value="cash">💰 เงินสด</SelectOption>
                    <SelectOption value="check">📝 เช็ค</SelectOption>
                    <SelectOption value="credit_card">💳 บัตรเครดิต</SelectOption>
                    <SelectOption value="other">🔧 อื่นๆ</SelectOption>
                  </Select>
                </div>
              </div>
            )}
            
            {editDialog.type === 'developer' && (
              <div className="space-y-6">
                {/* Developer Name Card */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="developer_name" className="text-lg font-semibold text-green-800">
                        ชื่อ Developer
                      </Label>
                      <p className="text-sm text-green-600">กรอกชื่อของ Developer</p>
                    </div>
                  </div>
                  <Input
                    id="developer_name"
                    value={editForm.developer_name || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, developer_name: e.target.value }))}
                    className="w-full border-green-300 focus:border-green-500 focus:ring-green-500"
                    placeholder="กรอกชื่อ Developer"
                  />
                </div>
                
                {/* Payment Date Card */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="payment_date" className="text-lg font-semibold text-blue-800">
                        วันที่จ่าย
                      </Label>
                      <p className="text-sm text-blue-600">เลือกวันที่ที่จ่ายเงินให้ Developer</p>
                    </div>
                  </div>
                  <Input
                    id="payment_date"
                    type="date"
                    value={editForm.payment_date || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, payment_date: e.target.value }))}
                    className="w-full border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
            
            {editDialog.type === 'expense' && (
              <div className="space-y-6">
                {/* Expense Name Card */}
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-orange-500 rounded-lg">
                      <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="expense_name" className="text-lg font-semibold text-orange-800">
                        ชื่อค่าใช้จ่าย
                      </Label>
                      <p className="text-sm text-orange-600">กรอกชื่อของค่าใช้จ่าย</p>
                    </div>
                  </div>
                  <Input
                    id="expense_name"
                    value={editForm.expense_name || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, expense_name: e.target.value }))}
                    className="w-full border-orange-300 focus:border-orange-500 focus:ring-orange-500"
                    placeholder="กรอกชื่อค่าใช้จ่าย"
                  />
                </div>
                
                {/* Amount Card */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-purple-500 rounded-lg">
                      <DollarSign className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="amount" className="text-lg font-semibold text-purple-800">
                        จำนวนเงิน
                      </Label>
                      <p className="text-sm text-purple-600">กรอกจำนวนเงินของค่าใช้จ่าย</p>
                    </div>
                  </div>
                  <Input
                    id="amount"
                    type="number"
                    value={editForm.amount || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    className="w-full border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                    placeholder="0.00"
                  />
                </div>
                
                {/* Description Card */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-gray-500 rounded-lg">
                      <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="description" className="text-lg font-semibold text-gray-800">
                        รายละเอียด
                      </Label>
                      <p className="text-sm text-gray-600">กรอกรายละเอียดเพิ่มเติม</p>
                    </div>
                  </div>
                  <Input
                    id="description"
                    value={editForm.description || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border-gray-300 focus:border-gray-500 focus:ring-gray-500"
                    placeholder="กรอกรายละเอียด"
                  />
                </div>
                
                {/* Date Card */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="expense_date" className="text-lg font-semibold text-blue-800">
                        วันที่
                      </Label>
                      <p className="text-sm text-blue-600">เลือกวันที่ของค่าใช้จ่าย</p>
                    </div>
                  </div>
                  <Input
                    id="expense_date"
                    type="date"
                    value={editForm.expense_date || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, expense_date: e.target.value }))}
                    className="w-full border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                {/* Payment Channel Card */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <PaymentIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="payment_channel" className="text-lg font-semibold text-green-800">
                        ช่องทางการชำระ
                      </Label>
                      <p className="text-sm text-green-600">เลือกวิธีการชำระเงิน</p>
                    </div>
                  </div>
                  <Select
                    value={editForm.payment_channel || 'transfer'}
                    onChange={(e) => setEditForm(prev => ({ ...prev, payment_channel: e.target.value }))}
                    className="w-full border-green-300 focus:border-green-500 focus:ring-green-500"
                  >
                    <SelectOption value="transfer">💳 โอนเงิน</SelectOption>
                    <SelectOption value="cash">💰 เงินสด</SelectOption>
                    <SelectOption value="check">📝 เช็ค</SelectOption>
                    <SelectOption value="credit_card">💳 บัตรเครดิต</SelectOption>
                    <SelectOption value="other">🔧 อื่นๆ</SelectOption>
                  </Select>
                </div>
              </div>
            )}
          </div>
          
          {/* Enhanced Footer */}
          <div className="px-6 pb-6">
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={handleCancelEdit} 
                className="flex-1 h-12 text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
              >
                <X className="h-4 w-4 mr-2" />
                ยกเลิก
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                className={`flex-1 h-12 text-white font-semibold ${
                  editDialog.type === 'payment' ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700' :
                  editDialog.type === 'developer' ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' :
                  'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
                }`}
              >
                <Check className="h-4 w-4 mr-2" />
                บันทึก
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDataTables;
